import { useState, ReactNode } from 'react';

interface ServicePageLayoutProps {
  blindType: string;
  heroImage: string;
  heroTitle: string;
  heroSubtitle: string;
  introText: string;
  features: Array<{ icon: string; title: string; desc: string }>;
  pricePerBlind: string;
  wholeHousePrice: string;
  benefits?: Array<{ icon: string; text: string }>;
  faqs: Array<{ question: string; answer: string }>;
  additionalContent?: ReactNode;
  children?: ReactNode;
  // Shutters page extra props (ignored gracefully)
  title?: string;
  subtitle?: string;
  introTitle?: string;
  formSubmitting?: boolean;
  formMessage?: string;
  onFormSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  selectedBlind?: string;
}

export default function ServicePageLayout({
  blindType,
  heroImage,
  heroTitle,
  heroSubtitle,
  introText,
  features,
  pricePerBlind,
  wholeHousePrice,
  benefits = [],
  faqs,
  additionalContent,
  children,
}: ServicePageLayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [blindsMenuOpen, setBlindsMenuOpen] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formMessage, setFormMessage] = useState('');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const scrollToQuote = () => {
    document.getElementById('quote-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d6qitntsbgrc47fim72g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormMessage("Thank you! We'll contact you within 2 hours.");
        form.reset();
      } else {
        setFormMessage('Something went wrong. Please call us directly.');
      }
    } catch {
      setFormMessage('Something went wrong. Please call us directly.');
    } finally {
      setFormSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Fixed Red Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#CC0000] py-2 sm:py-3 shadow-lg">
        <div className="max-w-7xl mx-auto px-3 sm:px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <h1>
                <a href="/" title="Blinds and Shutters Northampton">
                  <img
                    alt="Blinds and Shutters Northampton"
                    className="h-10 sm:h-12 w-auto brightness-0 invert"
                    src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/42a1287a39988fc1ade15e36e3abd7bd.png"
                  />
                </a>
              </h1>
            </div>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-6">
              <a href="/" className="text-white text-sm font-medium hover:text-white/80 transition-colors cursor-pointer">Home</a>
              <div className="relative group">
                <button className="text-white text-sm font-medium hover:text-white/80 transition-colors cursor-pointer flex items-center gap-1 py-2">
                  Blinds & Shutters
                  <i className="ri-arrow-down-s-line text-base transition-transform duration-300 group-hover:rotate-180"></i>
                </button>
                <div className="absolute top-full left-0 pt-1 w-56 transition-all duration-300 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 -translate-y-2">
                  <div className="bg-white rounded-lg shadow-xl border border-gray-100 py-2">
                    {['Faux Wood Blinds','Real Wood Blinds','Roller Blinds','Vertical Blinds','Day & Night Blinds','Perfect Fit Blinds','Pleated Blinds','Venetian Blinds','Shutters'].map((item) => (
                      <a key={item} href={`/${item.toLowerCase().replace(/\s+/g,'-').replace(/&/g,'')}`} className="block px-4 py-2.5 text-sm text-gray-900 hover:bg-[#CC0000]/10 hover:text-[#CC0000] transition-colors cursor-pointer">{item}</a>
                    ))}
                  </div>
                </div>
              </div>
              <a href="/blog" className="text-white text-sm font-medium hover:text-white/80 transition-colors cursor-pointer">Blog</a>
              <a href="/contact" className="text-white text-sm font-medium hover:text-white/80 transition-colors cursor-pointer">Contact</a>
            </div>

            {/* Pricing text */}
            <div className="hidden md:flex items-center gap-2">
              <i className="ri-fire-fill text-yellow-300 text-lg animate-pulse"></i>
              <span className="text-white/90 text-xs sm:text-sm font-medium">Whole House from £849 | £119 per blind fitted</span>
            </div>

            {/* Phone + Hamburger */}
            <div className="flex items-center gap-2 sm:gap-3">
              <a
                href="tel:01604389124"
                className="bg-white text-[#CC0000] px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5"
              >
                <i className="ri-phone-fill"></i>
                <span className="hidden sm:inline">01604 389 124</span>
                <span className="sm:hidden">Call</span>
              </a>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 cursor-pointer text-white"
                aria-label="Toggle menu"
              >
                <i className={`text-2xl transition-transform duration-300 ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>

          {/* Mobile pricing strip */}
          <div className="md:hidden flex items-center justify-center gap-2 mt-2 pt-2 border-t border-white/20">
            <i className="ri-fire-fill text-yellow-300 text-sm animate-pulse"></i>
            <span className="text-white/90 text-xs">Whole House from £849 | £119 per blind fitted</span>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden bg-[#AA0000] overflow-hidden transition-all duration-500 ease-in-out ${mobileMenuOpen ? 'max-h-[70vh] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="px-4 py-4 space-y-1 overflow-y-auto max-h-[70vh]">
            <a href="/" className="block w-full text-left text-white hover:bg-white/10 font-medium cursor-pointer py-3 px-4 rounded-lg transition-all duration-300">Home</a>
            <div className="border-t border-white/20 pt-2 mt-2">
              <button
                onClick={() => setBlindsMenuOpen(!blindsMenuOpen)}
                className="flex items-center justify-between w-full text-left text-white font-semibold cursor-pointer py-3 px-4 rounded-lg transition-colors hover:bg-white/10"
              >
                <span>Blinds & Shutters</span>
                <i className={`ri-arrow-down-s-line text-xl transition-transform duration-300 ${blindsMenuOpen ? 'rotate-180' : ''}`}></i>
              </button>
              <div className={`overflow-hidden transition-all duration-500 ease-in-out ${blindsMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="pl-4 space-y-1 pt-2">
                  {['Faux Wood Blinds','Real Wood Blinds','Roller Blinds','Vertical Blinds','Day & Night Blinds','Perfect Fit Blinds','Pleated Blinds','Venetian Blinds','Shutters'].map((item) => (
                    <a key={item} href={`/${item.toLowerCase().replace(/\s+/g,'-').replace(/&/g,'')}`} className="block w-full text-left text-white/90 hover:text-white hover:bg-white/10 font-medium cursor-pointer py-2.5 px-4 rounded-lg transition-all duration-300">
                      <i className="ri-arrow-right-s-line mr-2"></i>{item}
                    </a>
                  ))}
                </div>
              </div>
            </div>
            <div className="border-t border-white/20 pt-2 mt-2">
              <a href="/contact" className="block w-full text-left text-white hover:bg-white/10 font-medium cursor-pointer py-3 px-4 rounded-lg transition-all duration-300">Contact</a>
            </div>
            <a
              href="tel:01604389124"
              className="flex items-center justify-center gap-2 bg-white text-[#CC0000] px-6 py-4 rounded-full text-base font-semibold text-center hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer mt-4"
            >
              <i className="ri-phone-fill text-lg"></i>
              Call 01604 389 124
            </a>
          </div>
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-[72px] sm:h-[80px]"></div>

      {/* Hero Section */}
      <section className="relative min-h-[60vh] sm:min-h-[70vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${heroImage})` }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 mb-4">
              <i className="ri-map-pin-line text-[#CC0000]"></i>
              <span className="text-white text-xs sm:text-sm font-medium">Northampton & Surrounding Areas</span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 sm:mb-4 leading-tight">
              <strong>{heroTitle}</strong>
            </h1>

            <p className="text-lg sm:text-xl md:text-2xl text-white/90 mb-6 sm:mb-8 font-light">
              {heroSubtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={scrollToQuote}
                className="bg-[#CC0000] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-bold hover:bg-[#AA0000] transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-gift-line"></i>
                Get Free Quote
              </button>
              <a
                href="tel:01604389124"
                className="bg-white text-gray-900 px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-bold hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-phone-fill text-[#CC0000]"></i>
                01604 389 124
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-10 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-6">
              {introText}
            </p>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {benefits.map((benefit, index) => (
                <span key={index} className="bg-[#CC0000]/10 text-[#CC0000] px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium flex items-center gap-1.5">
                  <i className={benefit.icon}></i> {benefit.text}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-10 sm:py-16 bg-[#F6F6F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Why Choose {blindType}?
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-[#CC0000]/10 rounded-xl flex items-center justify-center mb-4">
                  <i className={`${feature.icon} text-[#CC0000] text-2xl`}></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2 text-base sm:text-lg">{feature.title}</h4>
                <p className="text-gray-600 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-10 sm:py-16 bg-gradient-to-br from-red-50 to-rose-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <span className="inline-block bg-[#CC0000]/10 text-[#CC0000] text-xs sm:text-sm font-bold px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-3 sm:mb-4">UNBEATABLE PRICES</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              {blindType} Pricing
            </h2>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg text-center">
                <span className="inline-block bg-[#CC0000]/10 text-[#CC0000] text-xs font-bold px-3 py-1 rounded-full mb-4">SINGLE BLIND</span>
                <div className="text-5xl sm:text-6xl font-bold text-[#CC0000] mb-2">{pricePerBlind}</div>
                <p className="text-gray-700 font-medium text-lg mb-6">per blind supplied & installed</p>
                <ul className="space-y-2 text-left mb-6">
                  <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-[#CC0000]"></i> Professional measurement</li>
                  <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-[#CC0000]"></i> Expert installation</li>
                  <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-[#CC0000]"></i> Quality guarantee</li>
                </ul>
                <button onClick={scrollToQuote} className="w-full bg-gray-900 text-white py-3 rounded-full font-bold hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer">
                  Get Quote
                </button>
              </div>

              <div className="bg-[#CC0000] text-white rounded-2xl p-6 sm:p-8 shadow-xl text-center relative overflow-hidden">
                <div className="absolute top-4 right-4 bg-yellow-400 text-gray-900 text-xs font-bold px-3 py-1 rounded-full">BEST VALUE</div>
                <span className="inline-block bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full mb-4">WHOLE HOUSE</span>
                <div className="text-5xl sm:text-6xl font-bold mb-2">{wholeHousePrice}</div>
                <p className="text-white/90 font-medium text-lg mb-6">Up to 8 windows supplied & fitted</p>
                <ul className="space-y-2 text-left mb-6">
                  <li className="flex items-center gap-2"><i className="ri-check-line"></i> Unbeatable value</li>
                  <li className="flex items-center gap-2"><i className="ri-check-line"></i> Complete home solution</li>
                  <li className="flex items-center gap-2"><i className="ri-check-line"></i> Professional service</li>
                </ul>
                <button onClick={scrollToQuote} className="w-full bg-white text-[#CC0000] py-3 rounded-full font-bold hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer">
                  Claim This Offer
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Content */}
      {additionalContent && additionalContent}

      {/* FAQ Section */}
      <section className="py-10 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-[#F6F6F6] rounded-xl overflow-hidden">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <span className="font-bold text-gray-900 pr-4">{faq.question}</span>
                  <i className={`ri-arrow-down-s-line text-[#CC0000] text-xl transition-transform duration-300 shrink-0 ${expandedFaq === index ? 'rotate-180' : ''}`}></i>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${expandedFaq === index ? 'max-h-96' : 'max-h-0'}`}>
                  <div className="p-5 pt-0 text-gray-700">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section id="quote-section" className="py-10 sm:py-16 lg:py-20 bg-gradient-to-br from-red-50 to-rose-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div>
              <span className="inline-block bg-[#CC0000]/10 text-[#CC0000] text-xs sm:text-sm font-bold px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-3 sm:mb-4">FREE QUOTE</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
                Get Your <strong className="text-[#CC0000]">Free Quote Today</strong>
              </h2>
              <p className="text-gray-600 text-sm sm:text-lg mb-6 sm:mb-8">
                Ready to upgrade your home with beautiful {blindType.toLowerCase()}? Contact us today for a free, no-obligation quote.
              </p>
              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                {['Fast quotes','Professional installation','High quality blinds','Unbeatable prices'].map((item) => (
                  <div key={item} className="flex items-center gap-2 sm:gap-3">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#CC0000]/20 rounded-full flex items-center justify-center shrink-0">
                      <i className="ri-check-double-line text-[#CC0000] text-sm"></i>
                    </div>
                    <span className="text-gray-700 font-medium text-sm sm:text-base">{item}</span>
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg space-y-3">
                <div>
                  <p className="text-gray-600 mb-1 text-sm">Call us directly:</p>
                  <a href="tel:01604389124" className="text-2xl sm:text-3xl font-bold text-[#CC0000] hover:text-[#AA0000] transition-colors cursor-pointer flex items-center gap-2 sm:gap-3">
                    <i className="ri-phone-fill"></i>01604 389 124
                  </a>
                </div>
                <div className="border-t border-gray-200 pt-3">
                  <p className="text-gray-600 mb-1 text-sm">Or email us:</p>
                  <a href="mailto:info@blindsandshuttersnorthampton.co.uk" className="text-base sm:text-lg font-semibold text-[#CC0000] hover:text-[#AA0000] transition-colors cursor-pointer break-all">
                    info@blindsandshuttersnorthampton.co.uk
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-5 sm:p-6 lg:p-8">
              <form data-readdy-form onSubmit={handleFormSubmit} className="space-y-4 sm:space-y-5">
                <input type="hidden" value={blindType} name="source" />
                <input type="hidden" value={blindType} name="selected_blind" />
                <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label htmlFor="name" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <input id="name" required className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#CC0000] focus:border-[#CC0000] outline-none transition-all text-sm" placeholder="Your full name" name="name" />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                    <input id="phone" required className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#CC0000] focus:border-[#CC0000] outline-none transition-all text-sm" placeholder="Your phone number" type="tel" name="phone" />
                  </div>
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <input id="email" required className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#CC0000] focus:border-[#CC0000] outline-none transition-all text-sm" placeholder="your@email.com" type="email" name="email" />
                </div>
                <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label htmlFor="postcode" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Postcode *</label>
                    <input id="postcode" required className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#CC0000] focus:border-[#CC0000] outline-none transition-all text-sm" placeholder="e.g. NN1 1AA" name="postcode" />
                  </div>
                  <div>
                    <label htmlFor="windows" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Number of Windows</label>
                    <select id="windows" name="windows" className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#CC0000] focus:border-[#CC0000] outline-none transition-all text-sm cursor-pointer">
                      <option value="">Select...</option>
                      <option value="1-3">1-3 windows</option>
                      <option value="4-6">4-6 windows</option>
                      <option value="7-10">7-10 windows</option>
                      <option value="10+">10+ windows</option>
                      <option value="whole-house">Whole house</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="message" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Additional Details</label>
                  <textarea id="message" name="message" rows={3} maxLength={500} className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#CC0000] focus:border-[#CC0000] outline-none transition-all text-sm resize-none" placeholder="Tell us about your requirements (optional)"></textarea>
                </div>
                <button type="submit" disabled={formSubmitting} className="w-full bg-[#CC0000] text-white py-3 rounded-full font-bold hover:bg-[#AA0000] transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  <i className="ri-gift-line"></i>
                  {formSubmitting ? 'Sending...' : 'Get My Free Quote'}
                </button>
                {formMessage && (
                  <div className={`text-center text-sm p-3 rounded-lg ${formMessage.includes('Thank you') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {formMessage}
                  </div>
                )}
                <p className="text-[10px] sm:text-xs text-gray-500 text-center">We'll call you back within 2 hours • No spam, ever</p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <section className="bg-gray-900 py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 sm:mb-8">
            <div className="flex justify-center mb-4 sm:mb-6">
              <img alt="Blinds and Shutters Northampton Logo" className="h-12 sm:h-16 w-auto brightness-0 invert" src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/42a1287a39988fc1ade15e36e3abd7bd.png" />
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2 sm:mb-4">Ready to Transform Your Home?</h2>
            <p className="text-white/70 text-sm sm:text-base mb-4 sm:mb-6">Beautiful blinds. Local service. Unbeatable prices.</p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button onClick={scrollToQuote} className="bg-[#CC0000] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold hover:bg-[#AA0000] transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-sm sm:text-base">
                <i className="ri-arrow-up-line"></i>Get Free Quote
              </button>
              <a href="tel:01604389124" className="bg-white text-gray-900 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-sm sm:text-base">
                <i className="ri-phone-fill text-[#CC0000]"></i>Call 01604 389 124
              </a>
            </div>
          </div>

          <div className="bg-gradient-to-r from-[#CC0000] to-[#AA0000] rounded-xl sm:rounded-2xl p-4 sm:p-6 mt-6 sm:mt-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
              <div className="flex items-center gap-3 text-center sm:text-left">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center shrink-0">
                  <i className="ri-home-heart-line text-white text-xl sm:text-2xl"></i>
                </div>
                <div>
                  <p className="text-white font-bold text-sm sm:text-lg">Your Local Northampton Specialists</p>
                  <p className="text-white/80 text-xs sm:text-sm">Your home is in safe, experienced hands</p>
                </div>
              </div>
              <button onClick={scrollToQuote} className="bg-white text-[#CC0000] px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-bold hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base shrink-0">
                Get Started
              </button>
            </div>
          </div>

          <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-white/10">
            <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-8 text-white/60 text-xs sm:text-sm">
              <span className="flex items-center gap-1.5"><i className="ri-shield-check-line text-[#CC0000]"></i>Quality Guarantee</span>
              <span className="flex items-center gap-1.5"><i className="ri-home-heart-line text-[#CC0000]"></i>Local Family Business</span>
              <span className="flex items-center gap-1.5"><i className="ri-star-fill text-yellow-400"></i>Excellent Reviews</span>
            </div>
            <p className="text-center text-white/40 text-[10px] sm:text-xs mt-4">
              © 2025 Blinds and Shutters Northampton • Serving NN1-NN7 &amp; Surrounding Areas
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}